import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowDownToLine, ArrowUpFromLine, Pencil, Archive, RotateCcw, ShoppingCart, Scale } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { MOV_TYPE, fmtDate, fmtDateTime, fmtQty, fmtCHF, todayZurich } from "@/lib/format";
import { MapBadge, StatusBadge } from "@/components/StatusBadge";
import { Card, Empty, Spinner } from "@/components/Field";
import { ProductDialog } from "@/components/dialogs/ProductDialog";
import { MovementDialog } from "@/components/dialogs/MovementDialog";
import { ShoppingDialog } from "@/components/dialogs/ShoppingDialog";
import { AdjustDialog } from "@/components/dialogs/AdjustDialog";
import { ProductFlags } from "./InventoryPage";

export default function ProductDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const { data: p, reload, error } = useFetch(`/products/${id}`);
  const [dialog, setDialog] = useState(null);
  const [adjLot, setAdjLot] = useState(null);
  const close = (o) => !o && setDialog(null);
  const today = todayZurich();

  if (error) return <Empty testId="product-not-found" text="Prodotto non trovato." />;
  if (!p) return <Spinner />;
  const toggleArchive = async () => {
    try {
      await api.post(`/products/${id}/${p.status === "active" ? "archive" : "restore"}`);
      toast.success(p.status === "active" ? "Prodotto archiviato" : "Prodotto ripristinato");
      reload();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <>
      <Link to="/inventario" data-testid="product-back" className="inline-flex items-center gap-1 text-sm font-semibold text-primary"><ArrowLeft className="h-4 w-4" /> Inventario</Link>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 data-testid="product-name" className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{p.name}</h1>
            <ProductFlags p={p} />
          </div>
          <p className="text-slate-500 mt-1">{[p.category, p.location, p.code && `Cod. ${p.code}`, p.barcode && `EAN ${p.barcode}`].filter(Boolean).join(" · ")}</p>
          {p.description && <p className="mt-2 text-slate-700 max-w-2xl">{p.description}</p>}
        </div>
        <div className="rounded-xl border bg-white px-5 py-3 text-right">
          <div data-testid="product-quantity" className="font-heading text-3xl font-bold text-slate-900">{fmtQty(p.quantity)} <span className="text-base font-medium text-slate-500">{p.unit}</span></div>
          {p.min_stock != null && <div className="text-sm text-slate-500">Scorta minima {fmtQty(p.min_stock)}</div>}
          {(p.purchase_price != null || p.sale_price != null) && <div data-testid="product-prices" className="text-sm text-slate-500">{p.purchase_price != null && `Acquisto ${fmtCHF(p.purchase_price)}`}{p.purchase_price != null && p.sale_price != null && " · "}{p.sale_price != null && `Vendita ${fmtCHF(p.sale_price)}`}</div>}
        </div>
      </div>

      {p.low_stock && can("shopping", "create") && (
        <div data-testid="low-stock-suggestion" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
          <span>Il prodotto è sotto la scorta minima.{p.open_shopping > 0 && " C'è già una richiesta aperta nella lista della spesa."}</span>
          {!p.open_shopping && <Button data-testid="low-stock-add-shopping" className="h-10" onClick={() => setDialog("shop")}><ShoppingCart className="h-4 w-4 mr-1" /> Aggiungi alla spesa</Button>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {can("movements", "create") && p.status === "active" && <>
          <Button data-testid="product-entry-button" className="h-11" onClick={() => setDialog("in")}><ArrowDownToLine className="h-4 w-4 mr-1" /> Registra entrata</Button>
          <Button data-testid="product-exit-button" className="h-11" onClick={() => setDialog("out")} disabled={!p.quantity}><ArrowUpFromLine className="h-4 w-4 mr-1" /> Registra uscita</Button>
        </>}
        {can("shopping", "create") && <Button data-testid="product-shopping-button" variant="outline" className="h-11" onClick={() => setDialog("shop")}><ShoppingCart className="h-4 w-4 mr-1" /> Richiesta di acquisto</Button>}
        {can("inventory", "edit") && <Button data-testid="product-edit-button" variant="outline" className="h-11" onClick={() => setDialog("edit")}><Pencil className="h-4 w-4 mr-1" /> Modifica</Button>}
        {can("inventory", p.status === "active" ? "delete" : "edit") && <Button data-testid="product-archive-button" variant="outline" className="h-11" onClick={toggleArchive}>{p.status === "active" ? <><Archive className="h-4 w-4 mr-1" /> Archivia</> : <><RotateCcw className="h-4 w-4 mr-1" /> Ripristina</>}</Button>}
      </div>

      <Card testId="product-lots">
        <h2 className="font-heading font-semibold px-5 h-14 flex items-center border-b">Lotti</h2>
        {p.lots.length === 0 ? <p className="px-5 py-6 text-slate-500 text-sm">Nessun lotto. Registra un'entrata per creare il primo lotto.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left"><tr>{["Lotto", "Residuo", "Iniziale", "Entrata", "Scadenza", "Fornitore", "Ubicazione", ""].map((h) => <th key={h} className="px-4 py-2 font-semibold">{h}</th>)}</tr></thead>
              <tbody className="divide-y">
                {p.lots.map((l) => (
                  <tr key={l.id} data-testid={`lot-row-${l.id}`} className={l.qty_remaining <= 0 ? "text-slate-400" : ""}>
                    <td className="px-4 py-2.5 font-medium">{l.lot_number || "—"} {l.suggested && <StatusBadge label="Usa per primo" tone="info" />}</td>
                    <td className="px-4 py-2.5 font-semibold">{fmtQty(l.qty_remaining)}</td>
                    <td className="px-4 py-2.5">{fmtQty(l.qty_initial)}</td>
                    <td className="px-4 py-2.5">{fmtDate(l.entry_date)}</td>
                    <td className="px-4 py-2.5">{l.expiry_date ? <span className={l.expiry_date < today && l.qty_remaining > 0 ? "text-red-700 font-semibold" : ""}>{fmtDate(l.expiry_date)}</span> : "—"}</td>
                    <td className="px-4 py-2.5">{l.supplier || "—"}</td>
                    <td className="px-4 py-2.5">{l.location || "—"}</td>
                    <td className="px-4 py-2.5">{can("movements", "approve") && <Button data-testid={`lot-adjust-${l.id}`} variant="ghost" size="sm" onClick={() => setAdjLot(l)}><Scale className="h-4 w-4 mr-1" /> Rettifica</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card testId="product-movements">
        <h2 className="font-heading font-semibold px-5 h-14 flex items-center border-b">Ultimi movimenti</h2>
        {p.movements.length === 0 ? <Empty text="Nessun movimento." /> : (
          <ul className="divide-y">
            {p.movements.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0 text-sm">
                  <div className="font-medium text-slate-900">{m.type === "adjust" && m.quantity > 0 ? "+" : ""}{fmtQty(m.quantity)} {m.unit} {m.reason && `· ${m.reason}`}</div>
                  <div className="text-slate-500">{fmtDateTime(m.occurred_at)} · {m.recorded_by_name}{m.picked_up_by && m.picked_up_by !== m.recorded_by_name && ` · ritirato da ${m.picked_up_by}`}{m.event_name && ` · ${m.event_name}`}{m.total_cost != null && ` · costo ${fmtCHF(m.total_cost)}`}{m.invoice_number && ` · fattura ${m.invoice_number}`}</div>
                </div>
                <MapBadge map={MOV_TYPE} value={m.type} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ProductDialog open={dialog === "edit"} onOpenChange={close} product={p} onSaved={reload} />
      <MovementDialog open={dialog === "in"} mode="in" productId={id} onOpenChange={close} onSaved={reload} />
      <MovementDialog open={dialog === "out"} mode="out" productId={id} onOpenChange={close} onSaved={reload} />
      <ShoppingDialog open={dialog === "shop"} onOpenChange={close} onSaved={reload}
        preset={{ name: p.name, unit: p.unit, product_id: p.id, quantity: String(Math.max(1, (p.min_stock || 0) - (p.quantity || 0))) }} />
      <AdjustDialog open={!!adjLot} onOpenChange={(o) => !o && setAdjLot(null)} lot={adjLot} unit={p.unit} onSaved={reload} />
    </>
  );
}
